import { useEffect, useRef, useState, type ReactElement } from 'react';
import {
  Autocomplete as RACAutocomplete,
  Header,
  Input,
  ListBox,
  ListBoxItem,
  ListBoxSection,
  SearchField,
  type Key,
  Text,
} from 'react-aria-components';
import LoadingIcon from '@components/sds/icons/loading';
import styles from './autocomplete.module.scss';

export interface AutocompleteSuggestion {
  label: string;
  description?: string;
  value: Key;
}

export interface AutocompleteProps {
  inputValue: string;
  onInputChange: (value: string) => void;
  ['aria-label']: string;
  placeholder: string;
  items: AutocompleteSuggestion[];
  loading?: boolean;
  setValue: (value: string) => void;
  value?: Key;
  filterText?: string;
  startIcon?: ReactElement;
}

export const Autocomplete = ({
  placeholder,
  items,
  setValue,
  value,
  loading = false,
  filterText = '',
  startIcon,
  ...rest
}: AutocompleteProps) => {
  const [focused, setFocused] = useState(false);
  const autocompleteRef = useRef<HTMLDivElement>(null);

  const onSelect = (e: React.MouseEvent) => {
    const key =
      (e.currentTarget as HTMLElement).getAttribute('data-key') ??
      (e.target as HTMLElement).getAttribute('data-key') ??
      '';

    setValue(key);
    setFocused(false);
  };

  useEffect(() => {
    const handleClickOutside = (event: MouseEvent) => {
      // Check if the click target is outside the referenced element
      if (
        autocompleteRef.current &&
        !autocompleteRef.current?.contains(event.target as Node)
      ) {
        setFocused(false);
      }
    };

    // Attach the event listener to the document
    document.addEventListener('mousedown', handleClickOutside);

    // Clean up the event listener on component unmount
    return () => {
      document.removeEventListener('mousedown', handleClickOutside);
    };
  }, []);

  const searchFieldStyles = `${styles.searchField} ${focused ? styles.active : ''}`;

  return (
    <div className={styles.container} ref={autocompleteRef}>
      <RACAutocomplete {...rest}>
        <SearchField
          aria-label={rest['aria-label']}
          className={searchFieldStyles}
        >
          {startIcon}
          <Input onFocus={() => setFocused(true)} placeholder={placeholder} />
        </SearchField>
        {focused && filterText && filterText !== value && !loading && (
          <ListBox
            renderEmptyState={() => 'No results found.'}
            className={styles.listbox}
          >
            <ListBoxSection>
              <Header>Suggestions</Header>
              {items.map(item => (
                <ListBoxItem
                  key={item.value}
                  id={item.value}
                  textValue={item.label}
                  onClick={onSelect}
                  aria-label={item.label}
                >
                  {item.description ? (
                    <>
                      <Text slot="label">{item.label}</Text>
                      <Text slot="description">{item.description}</Text>
                    </>
                  ) : (
                    item.label
                  )}
                </ListBoxItem>
              ))}
            </ListBoxSection>
          </ListBox>
        )}

        {focused && filterText && loading && (
          <div className={styles.listbox}>
            <header>Suggestions</header>
            <div className={styles.loadingContainer}>
              <LoadingIcon />
            </div>
          </div>
        )}
      </RACAutocomplete>
    </div>
  );
};
